'use client';

import { useEffect, useState } from 'react';
import { Box } from '@wishket/design-system';

import {
  DoubleLayerCollectionThumbnail,
  SingleLayerCollectionThumbnail,
  TripleLayerCollectionThumbnail,
} from './CollectionThumbnail.parts';

import { ThumbnailSize } from './CollectionThumbnail.types';
import { lightenHexColor } from './CollectionThumbnail.utils';

import { PATH } from '@/shared/constants';
import { CollectionStatus } from '@/shared/types';

interface CollectionThumbnailProps {
  count?: number;
  size: ThumbnailSize;
  isChecked?: boolean;
  layerColor?: string;
  imgSrc?: string | null;
  isSelectable?: boolean;
  profileImgSrc?: string;
  status?: CollectionStatus;
  onEdit?: () => void;
  onChecked?: () => void;
}

const CollectionThumbnail = ({
  size,
  imgSrc,
  count = 0,
  isChecked,
  layerColor,
  isSelectable,
  profileImgSrc,
  status = CollectionStatus.PUBLIC,
  onEdit,
  onChecked,
}: CollectionThumbnailProps) => {
  const collectionThumbnail = {
    default: `${PATH.MEDIA_YOZM_IMAGE}/default_collection_thumbnail.svg`,
    private: `${PATH.MEDIA_YOZM_IMAGE}/private_collection_thumbnail.svg`,
    deleted: `${PATH.MEDIA_YOZM_IMAGE}/deleted_collection_thumbnail.svg`,
  };

  const generateImgSrc = () => {
    switch (status) {
      case CollectionStatus.PUBLIC:
        return imgSrc || collectionThumbnail.default;
      case CollectionStatus.PRIVATE:
        return collectionThumbnail.private;
      case CollectionStatus.DELETED:
        return collectionThumbnail.deleted;
    }
  };

  const [src, setSrc] = useState<string>(generateImgSrc());

  useEffect(() => {
    setSrc(generateImgSrc());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imgSrc]);

  const props = {
    count,
    isChecked,
    imgSrc: src,
    isSelectable,
    profileImgSrc,
    secondLayerColor: layerColor,
    thirdLayerColor: lightenHexColor(layerColor),
    onEdit,
    onChecked,
    onImgError: () => setSrc(collectionThumbnail.default),
  };

  return (
    <>
      <Box className="hidden tablet:hidden desktop:block">
        <TripleLayerCollectionThumbnail {...props} size={size.desktop} />
      </Box>
      <Box className="hidden tablet:block desktop:hidden">
        {size.tablet === 'xl' && (
          <TripleLayerCollectionThumbnail {...props} size={size.tablet} />
        )}
        {size.tablet === 'md' && (
          <DoubleLayerCollectionThumbnail {...props} size={size.tablet} />
        )}
        {size.tablet === 'sm' && (
          <DoubleLayerCollectionThumbnail {...props} size={size.tablet} />
        )}
      </Box>
      <Box className="tablet:hidden desktop:hidden">
        {size.mobile === 'xs' && (
          <DoubleLayerCollectionThumbnail {...props} size={size.mobile} />
        )}
        {size.mobile === 'sm' && (
          <DoubleLayerCollectionThumbnail {...props} size={size.mobile} />
        )}
        {size.mobile === 'full' && (
          <SingleLayerCollectionThumbnail {...props} />
        )}
      </Box>
    </>
  );
};

export default CollectionThumbnail;
